import { useBoard } from '@/features/project-board/BoardProvider';
import { OwnerRoster } from '@/features/project-board/owners';
import { EPIC_STAGE_COLUMNS } from '@/features/project-board/types';

const CONTROL = 'rounded-md border border-line bg-surface px-2.5 py-1.5';

/**
 * 화면 공통 필터. 어떤 컨트롤을 보여줄지는 화면이 정한다.
 *
 * 담당자 필터는 이슈 화면에도 걸린다 — 이슈의 `handledBy`(커밋 author)도 명부 기준으로 걸러 후보에
 * 오르므로(`ownerFacetOf`), 고른 담당자로 이슈도 좁혀진다.
 */
export function FilterBar({
  scope,
  countLabel,
  toolbar,
}: {
  scope: 'tasks' | 'issues' | 'incidents' | 'epics' | 'qa';
  countLabel: string;
  /** 화면이 제공하는 도구(뷰 전환 등). 화면마다 다르므로 슬롯으로 받는다. */
  toolbar?: React.ReactNode;
}): React.JSX.Element {
  const { index, filters, setFilters, loading, reload, epics } = useBoard();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      <input
        className={`${CONTROL} min-w-[200px] flex-1`}
        placeholder="제목 · ID · repo · 담당자 검색"
        value={filters.query}
        onChange={(event) => setFilters({ query: event.target.value })}
      />

      <select
        className={`${CONTROL} max-w-[220px]`}
        value={filters.owner}
        onChange={(event) => {
          const owner = event.target.value;
          const epic = OwnerRoster.epicSlugAfterOwnerChange(index?.epics ?? [], index?.tasks ?? [], filters.epic, owner);
          setFilters({ owner, epic });
        }}
      >
        <option value="">모든 담당자</option>
        {(index?.facets.owners ?? []).map((owner) => (
          <option key={owner} value={owner}>
            {owner}
          </option>
        ))}
      </select>

      {/* 에픽 목록 화면은 목록 자체가 에픽이라 에픽 선택이 필요 없다 — 검색창과 페이지네이션으로 찾는다. */}
      {scope !== 'epics' && (
        <select
          className={`${CONTROL} max-w-[220px]`}
          value={filters.epic}
          onChange={(event) => setFilters({ epic: event.target.value })}
        >
          <option value="">모든 에픽</option>
          {[...epics].sort((left, right) => left.slug.localeCompare(right.slug, 'en')).map((epic) => (
            <option key={epic.slug} value={epic.slug}>
              {epic.slug}
            </option>
          ))}
        </select>
      )}

      {scope === 'epics' && (
        <select
          className={`${CONTROL} max-w-[160px]`}
          value={filters.stage}
          onChange={(event) => setFilters({ stage: event.target.value })}
        >
          <option value="">모든 단계</option>
          {EPIC_STAGE_COLUMNS.map((column) => (
            <option key={column.key} value={column.key}>
              {column.label}
            </option>
          ))}
        </select>
      )}

      {scope === 'epics' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.hideDoneEpics}
            onChange={(event) => setFilters({ hideDoneEpics: event.target.checked })}
          />
          완료 에픽 숨김
        </label>
      )}

      {scope === 'issues' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.hideDoneIssues}
            onChange={(event) => setFilters({ hideDoneIssues: event.target.checked })}
          />
          완료 이슈 숨김
        </label>
      )}

      {scope === 'incidents' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.hideDoneIncidents}
            onChange={(event) => setFilters({ hideDoneIncidents: event.target.checked })}
          />
          완료 인시던트 숨김
        </label>
      )}

      {scope !== 'issues' && scope !== 'incidents' && scope !== 'qa' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.hideDropped}
            onChange={(event) => setFilters({ hideDropped: event.target.checked })}
          />
          제외 항목 숨김
        </label>
      )}

      {scope === 'tasks' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.onlyBlocked}
            onChange={(event) => setFilters({ onlyBlocked: event.target.checked })}
          />
          선행 미완만
        </label>
      )}

      {scope === 'tasks' && (
        <label className="flex items-center gap-1.5 whitespace-nowrap text-muted">
          <input
            type="checkbox"
            checked={filters.swimlane}
            onChange={(event) => setFilters({ swimlane: event.target.checked })}
          />
          에픽별 스윔레인
        </label>
      )}

      <span className="flex-1" />
      {toolbar}
      <span className="text-xs text-muted">{countLabel}</span>
      <button
        onClick={reload}
        disabled={loading}
        className={`${CONTROL} cursor-pointer hover:bg-line-soft disabled:cursor-default disabled:opacity-60`}
      >
        {loading ? '불러오는 중…' : '↻ 다시 불러오기'}
      </button>
    </div>
  );
}
